import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Seal from "@/components/Seal";
import { X } from "lucide-react";

const STORAGE_KEY = "wechr_cookie_consent";
export const COOKIE_CONSENT_EVENT = "wechr:open-cookie-consent";

// Allow other components (e.g. the footer) to reopen the banner.
export const openCookieConsent = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT));
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (!stored) {
      // Small delay so it animates in after first paint.
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  // Listen for manual reopen requests (e.g. from the footer link).
  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener(COOKIE_CONSENT_EVENT, handler);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch {
      /* ignore storage errors */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[300] px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4 rounded-2xl border border-[var(--wechr-border)] bg-white/85 p-5 shadow-xl backdrop-blur-md sm:flex-row sm:items-center sm:gap-6 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--wechr-subtle)]">
            <Seal size={26} />
          </div>
          <div className="text-sm text-navy-800">
            <p className="font-heading font-semibold text-navy-900">We value your privacy</p>
            <p className="mt-1 text-[13px] leading-relaxed text-[var(--wechr-muted-text)]">
              WECH-RB uses essential cookies to keep the platform secure and functioning, and optional
              cookies to understand how the site is used. See our{" "}
              <Link to="/privacy" className="font-medium text-wgreen-700 underline hover:text-wgreen-800">
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => persist("declined")}
            data-testid="cookie-decline-button"
          >
            Decline
          </Button>
          <Button
            className="btn-green"
            size="sm"
            onClick={() => persist("accepted")}
            data-testid="cookie-accept-button"
          >
            Accept all
          </Button>
          <button
            type="button"
            onClick={() => persist("dismissed")}
            aria-label="Dismiss cookie banner"
            className="ml-1 rounded-md p-1.5 text-[var(--wechr-muted-text)] transition-colors hover:bg-[var(--wechr-subtle)] hover:text-navy-900"
            data-testid="cookie-dismiss-button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
